import React, { Fragment, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import * as PropTypes from 'prop-types';

import Button from 'ati-ui-react/components/Button/v3';
import Skeleton from 'ati-ui-react/components/Skeleton';
import { simpleDayOfMonth, simpleFormatTime } from 'as-ui-kit/utils/time';
import { classes } from 'as-ui-kit/utils/styles';

import { useLocalized } from 'utils/useLocalized';
import { getRemainingTime } from 'utils/time';
import CountdownTimer from '../CountdownTimer';
import { DataContext, LoadingContext, LocaleContext } from 'contexts';
import styles from './styles.modules.scss';
import i18nStrings from './strings';
import AccessPayment from '../../AccessPayment';
import { PeriodType } from 'consts/consts';

const SearchPanel = ({handleSearchClick}) => {
    const { licenseInfo, isMobile, searchResults, isOutsideRussia } = useContext(DataContext);
    const { carTypesIsLoading, licenseInfoIsLoading, searchResultsIsLoading, filtersIsLoading } = useContext(LoadingContext);
    const locale = useContext(LocaleContext);
    const strings = useLocalized(i18nStrings);
    const [remaining, setRemaining] = useState();

    const hasAccess = useMemo(() => licenseInfo && licenseInfo.searchAccessFinishUtc, [licenseInfo]);
    const timerJSX = useMemo(() => <CountdownTimer licenseInfo={licenseInfo}/>, [licenseInfo]);
    const searchClicked = useMemo(() => !searchResults || searchResultsIsLoading, [searchResults, searchResultsIsLoading]);

    const payInfoJSX = useMemo(() => {
        if (!licenseInfo.searchAccessPrices) {
            return null;
        }

        const discount = licenseInfo.searchAccessPrices[PeriodType.day].discount;
        if (discount && discount > 0) {
            return (
                <Fragment>
                    <div className={styles['begin']}>
                        {strings.accessToEndOfDay}
                    </div>
                    <div className={styles['discount']}>
                        <div className={styles['base-price']}>
                            {`${licenseInfo.searchAccessPrices[PeriodType.day].base} ${strings.currency}`}
                        </div>
                        <div className={styles['end']}>
                            {`${licenseInfo.searchAccessPrices[PeriodType.day].personal} ${strings.currency}`}
                        </div>
                    </div>
                </Fragment>
            );
        }

        return `${strings.accessToEndOfDay} — ${licenseInfo.searchAccessPrices[PeriodType.day].personal} ${strings.currency}`;
    }, [strings, licenseInfo]);

    const accessInfoJSX = useMemo(() => {
        if (!hasAccess && remaining) {
            return (
                <div className={styles['offer']}>
                    <div className={styles['price']}>
                        {payInfoJSX}
                    </div>
                </div>
            );
        }

        const accessTime = new Date(licenseInfo.searchAccessFinishUtc);
        return (
            <div className={styles['payed-info']}>
                {`${strings.accessTo} ${simpleFormatTime(accessTime)}, ${simpleDayOfMonth(accessTime, locale)}.`}
            </div>
        );
    }, [hasAccess, remaining, payInfoJSX, licenseInfo, strings, locale]);

    const searchBtnJSX = useMemo(() => (
        carTypesIsLoading || licenseInfoIsLoading || filtersIsLoading ?
            (
                <Skeleton
                    borderRadius={2}
                    className={styles['search-button']}
                    height={42}
                    width={isMobile ? '100%' : locale === 'ru' ? '191px' : '141px'}
                />
            ) : (
                <div className={styles['search-button-block']}>
                    <Button
                        className={classes(styles['search-button'], (isMobile && styles['mobile-btn']))}
                        filled={isOutsideRussia || hasAccess}
                        freeze={searchClicked}
                        loading={searchClicked}
                        theme={isOutsideRussia && !hasAccess ? 'success' : 'default'}
                        onClick={handleSearchClick}
                    >
                        {strings.search}
                    </Button>
                    {isOutsideRussia && !hasAccess && (
                        <span className={styles['free-label']}>
                            <b>
                                {strings.free}
                            </b>
                            {' '}
                            {strings.outsideRussia}
                        </span>
                    )}
                </div>
            )
    ), [carTypesIsLoading, licenseInfoIsLoading, filtersIsLoading, hasAccess, isMobile, isOutsideRussia, handleSearchClick, searchClicked, strings, locale]);

    const panel = useMemo(() => {
        if (licenseInfoIsLoading) {
            return (
                <Skeleton
                    borderRadius={2}
                    className={styles['access']}
                    height={42}
                    width={isMobile ? '100%' : '330px'}
                />
            );
        } else if (hasAccess) {
            return (
                <div className={styles['access']}>
                    {accessInfoJSX}
                    {timerJSX}
                </div>
            );
        } else {
            return (
                <div className={styles['pay']}>
                    {accessInfoJSX}
                    <AccessPayment/>
                </div>
            );
        }
    },[licenseInfoIsLoading, isMobile, hasAccess, accessInfoJSX, timerJSX]);

    const calculateRemainingValue = useCallback(() => {
        if (!remaining) {
            setRemaining(getRemainingTime(locale));
        } else {
            setTimeout(() => {
                setRemaining(getRemainingTime(locale));
            }, 10000);
        }
    }, [remaining, locale]);

    useEffect(() => {
        calculateRemainingValue();
    }, [calculateRemainingValue]);

    return (
        <div className={classes(styles['panel'], (!licenseInfoIsLoading && !hasAccess && styles['no-access']))}>
            {panel}
            {searchBtnJSX}
        </div>
    );

};

export default SearchPanel;

SearchPanel.propTypes = {
    handleSearchClick: PropTypes.func
};
